import { Button, Css } from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { useRouteParams } from "src/hooks/useRouteParams";
import { disableBasedOnPotentialOperationText } from "src/routes/components/PotentialOperationsUtils";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PlaceholderProductSearch } from "src/routes/libraries/design-catalog/components/PlaceholderProductSearch";
import { useProductSearchPage_CurrentMaterialQuery } from "src/routes/libraries/design-catalog/design-package-configurator/routes/search/ProductSearchPage.generated";
import { DesignPackageParams } from "src/routes/routesDef";
import {
  createDesignPackageAddProductUrl,
  createDesignPackageProductCardUrl,
  createDesignPackageUrl,
  createDesignPackagesUrl,
} from "src/RouteUrls";
import { fail } from "src/utils";
import { h100ExclHdr } from "../../DesignPackageConfigurator";
import { useDesignPackageConfiguratorContext } from "../../DesignPackageConfiguratorContext";

/**
 * Shows all the products that match the requirements of a placeholder, to be selected
 * (and then configured in the next step), for a specific product card.
 *
 * This page can be render in 2 modes: Placeholder-only (create mode, we don't have a product assigned to the
 * product card yet) or Placeholder+Product (edit mode, we've already assigned a product, highlighted in blue,
 * but are potentially swapping to a new product).
 */
export function ProductSearchPage() {
  const { placeholderTliId, productTliId } = useRouteParams<DesignPackageParams>();
  const { designPackage } = useDesignPackageConfiguratorContext();
  const navigate = useNavigate();

  // Used for highlighting the currently-selected MaterialVariant (which may not exist
  // if we're creating a new product card, or the previous card had noBidItem)
  const productTli = useProductSearchPage_CurrentMaterialQuery({
    variables: { productTliId: productTliId!, versionId: designPackage.version.id },
    skip: !productTliId,
  }).data?.takeoffLineItem;

  return (
    <div css={{ ...h100ExclHdr, ...Css.w100.$ }}>
      <PageHeader
        title="Product Search"
        breadcrumb={[
          { href: createDesignPackagesUrl(), label: "Design Packages" },
          { href: createDesignPackageUrl(designPackage.id, designPackage.version.id), label: designPackage.name },
        ]}
        backButton={() => navigate(-1)}
        right={
          <Button
            label="Add New Product"
            disabled={disableBasedOnPotentialOperationText(designPackage.canEdit)}
            onClick={createDesignPackageAddProductUrl(
              designPackage.id ?? fail("Design Package ID is required"),
              designPackage.version.id,
              placeholderTliId,
              productTliId!,
            )}
          />
        }
      />
      <div css={Css.w100.px2.$}>
        <PlaceholderProductSearch
          placeholderTliId={placeholderTliId}
          showNoMaterialVariant
          currentMaterials={
            productTli?.isDisabledMaterialVariant
              ? { kind: "noMaterialVariant" }
              : productTli?.materialVariant
                ? { kind: "materials", ids: [productTli.materialVariant.id] }
                : undefined
          }
          onMaterialSelected={(selection) => {
            // When the material is selected, navigate to the Product Card page to have it do the "select"
            const materialVariantId = selection.kind === "noMaterialVariant" ? "noMaterialVariant" : selection.id;
            navigate(
              createDesignPackageProductCardUrl(
                designPackage.id,
                designPackage.version.id,
                placeholderTliId,
                productTli?.id,
                materialVariantId,
              ),
            );
          }}
        />
      </div>
    </div>
  );
}
